import type { ApiResponse, ListCreatorsResponse } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';
import type { CreatorFilters } from '@/types/filters';

export const fetchCreatorsList = async (
  filters: CreatorFilters = {}
): Promise<ListCreatorsResponse> => {
  // Build query parameters
  const params = new URLSearchParams();

  // Always include page
  params.append('page', (filters.page || 1).toString());

  if (filters.perPage) {
    params.append('per_page', filters.perPage.toString());
  }

  // Only include search if it has more than 1 character
  if (filters.search && filters.search.length > 1) {
    params.append('search', filters.search);
  }

  // Include other filters if they exist and are not empty
  if (filters.platform) {
    params.append('platform', filters.platform);
  }

  if (filters.status) {
    params.append('status', filters.status);
  }

  const { data } = await api.get<ApiResponse<ListCreatorsResponse>>(
    `/dashboard/creators/search?${params.toString()}`
  );

  if (!data.data) {
    throw new Error('Invalid response structure');
  }

  return data.data;
};

export const useSearchCreatorsQuery = (filters: CreatorFilters = {}, enabled = true) => {
  return useQuery<ListCreatorsResponse, Error>({
    queryKey: [
      'creators',
      'search',
      filters.page || 1,
      filters.perPage,
      filters.search,
      filters.platform,
      filters.status,
    ],
    queryFn: () => fetchCreatorsList(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
};
